import { useQuery, UseQueryResult } from '@tanstack/react-query';
import * as chatService from '../services/chatService';
import type { ChatSession } from '../services/chatService';

/** Derive a coarse activity status from last_activity (lifted from Sessions.tsx). */
function getSessionStatus(lastActivity: string): 'active' | 'idle' | 'archived' {
  const diffHours = (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60);
  if (diffHours < 1) return 'active';
  if (diffHours < 24) return 'idle';
  return 'archived';
}

/**
 * Single source of truth for the ['chatSessions'] query + post-mapping.
 * Sessions.tsx, ChatSessions.tsx, and the NewChatPicker all consume this so the
 * react-query cache key never binds to two different queryFns (first-to-bind-wins —
 * feedback_shared_resource_refcounts).
 */
export function useChatSessions(): UseQueryResult<ChatSession[]> {
  return useQuery<ChatSession[]>(['chatSessions'], async () => {
    const data = await chatService.getChatSessions();
    return data
      .filter(s => (s.message_count || 0) > 1) // drop system-prompt-only / non-chat leaks
      .map(session => ({
        ...session,
        status: getSessionStatus(session.last_activity),
        persona_specialization: session.persona_specialization || 'AI Assistant',
      }));
  });
}
